import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useEffect, useState, type FC } from "react";
import Text from "@/components/Text";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { confirmEmailUser, userState } from "@/store/userState";
import z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { resendSignUpCode } from "aws-amplify/auth";
import type { State } from "@/types/common";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const getMessageError = (
  userError: Error,
  t: TFunction<"translation", undefined>,
): string => {
  if (["NetworkError"].includes(userError.message.split(":")[0]))
    return t("network_error");

  if (userError.message === "username is required to signUp")
    return t("email_required");

  if (userError.message === "Username/client id combination not found.")
    return t("account_not_found");

  if (
    userError.message ===
    "UserNotFoundException: Username/client id combination not found."
  )
    return t("account_not_found");

  if (
    userError.message ===
    "CodeMismatchException: Invalid verification code provided, please try again."
  )
    return t("code_incorrect");

  return userError.message;
};

const formSchema = z.object({
  email: z.email({ error: "Email invalide" }),
  code: z.string().min(6),
});

const SignUpOTP: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    isLoading: userIsLoading,
    error: userError,
    confirmed,
    email,
  } = userState();
  const navigate = useNavigate();
  const [codeState, setCodeState] = useState<State<unknown>>({
    isLoading: false,
    error: null,
    data: null,
    isFetching: false,
  });

  const transformedPathname = pathname.replace("/confirm", "");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email,
      code: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    confirmEmailUser(values);
  };

  useEffect(() => {
    if (confirmed) {
      toast.success(t("signup_account_validated"), {
        duration: 2000,
      });
      navigate(`${transformedPathname}/sign-in`);
    }
  }, [confirmed, transformedPathname]);

  const sendNewCode = async (email: string) => {
    setCodeState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    try {
      const response = await resendSignUpCode({ username: email });

      toast.success(t("code_sent_on_email", { email }));

      setCodeState({
        isLoading: false,
        data: response,
        error: null,
        isFetching: false,
      });
    } catch (err) {
      setCodeState({
        isLoading: false,
        data: null,
        isFetching: false,
        error: err as Error,
      });
    }
  };

  return (
    <DialogContent showCloseButton={false}>
      <VisuallyHidden>
        <DialogTitle>Sign In</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Sign In here</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <Text fontWeight={600} fontSize={20} color="#44546F">
          {t("auth_title")}
        </Text>
        <Text fontWeight={400} fontSize={14} color="#44546F">
          {t("auth_subtitle")}
        </Text>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="gap-[12px] flex flex-col">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("email")}</FormLabel>
                  <FormControl>
                    <Input placeholder="johndoe@gmail.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputOTP maxLength={6} {...field}>
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>
                  <FormDescription>
                    {t("enter_6_digit_code_sent_to_email")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="h-px w-full bg-[#F1F2F4] my-[24px]" />
          <div className="flex gap-[8px]">
            <Button
              type="button"
              variant="secondaryFilled"
              onClick={async () => {
                await sendNewCode(form.getValues("email"));
              }}
            >
              {t("resend_code")}
            </Button>
            <Button
              type="submit"
              variant="primaryFilled"
              style={{ width: "calc(60% - 16px)" }}
              disabled={!form.formState.isValid || userIsLoading}
            >
              {t("confirm_email")}
            </Button>
          </div>
        </form>
      </Form>
      {userError &&
        userError.message.split(":")[0] !== "UserUnAuthenticatedException" && (
          <Alert variant="destructive">
            <AlertTitle>{t("auth_error_title")}</AlertTitle>
            <AlertDescription>{getMessageError(userError, t)}</AlertDescription>
          </Alert>
        )}
      {codeState.error && codeState.error instanceof Error && (
        <Alert variant="destructive">
          <AlertTitle>{t("auth_error_title")}</AlertTitle>
          <AlertDescription>
            {getMessageError(codeState.error, t)}
          </AlertDescription>
        </Alert>
      )}
    </DialogContent>
  );
};

export default SignUpOTP;
